/**
 * Service for some helpers for client-side rendering of nunjucks templates
 */

import { getTranslationStr } from '../localization';


/**
 * gets a translated value based on a key from BZFD
 * @param  {String} key -the translated (key of) the text wanted
 * @param  {String} defaultVal - default value in case BZFD undefined
 * @return {String} the value in BZFD localization or default val
 */
function _getI18nValue(key, defaultVal = '') {
  try {
    let translated = getTranslationStr(key);
    return translated.length <= 0 ? defaultVal : translated;
  } catch (err) {
    console.error('error fetching translation: ' + err);
  }
  return defaultVal;
}

/**
 * prepends a selected click tracker
 * @param  {String} tracker - the redirect url tracker
 * @param  {String} url - the url to prepend the tracker to
 * @return {String} the full url
 */
function _prependClickTracker(tracker, url) {
  if(!tracker || tracker.length < 0) {
    return url;
  }

  return tracker + encodeURIComponent(url);
}

/**
 * attach filters to nunjucks environment
 * @param  {NunjucksTemplate} tpl
 * @return {NunjucksTemplate} returns the same template w/ filters
 * attached to the environment
 */
function _attachFilters(tpl) {
  if(!tpl.env) {
    return tpl;
  }

  /**
   * nunjucks usage:
   * {{ config | dump }}
   * {{ comfig | tojson }} -> to mirror jinja
   */
  tpl.env.addFilter('tojson', tpl.env.filters.dump); // alias

  /**
   * nunjucks usage:
   * {{ data.someUrl | prependClickTracker(ctx.dfpClickTracker) }}
   * {{ "someUrlString" | prependClickTracker(ctx.vendorClickTracker) }}
   */
  tpl.env.addFilter('prependClickTracker', (url, tracker) => {
    return _prependClickTracker(tracker, url);
  });

  /**
   * nunjucks usage:
   * {{ "Promoted By" | l10n('PROMOTED_BY') }}
   */
  tpl.env.addFilter('l10n', (defaultValue, key) => {
    return _getI18nValue(key, defaultValue);
  });

  /**
   * nunjucks usage:
   * {{ "some_string" | regex_match('^some') }}
   */
  tpl.env.addFilter('regex_match', (str, pattern) => str.match(new RegExp(pattern)));

  return tpl;
}

/**
 * Make custom jinja helpers available in nunjucks template on frontend
 * @see https://github.com/buzzfeed/mono/blob/master/packages/python/bf_site_rendering/bf_site_rendering/view.py
 * @todo: `static_url`, `static_file_path`, `pixiedust_pixel_url`?
 */
function _attachGlobals(tpl) {
  tpl.env.addGlobal('i18n', window.BZFD.Context.page.localization.translations);
  tpl.env.addGlobal(
    'utils',
    {
      'transGif1x1Base64': 'data:image/gif;base64,R0lGODlhAQABAPAAAAAAAAAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==',
      'transGif3x2Base64': 'data:image/gif;base64,R0lGODlhAwACAPAAAAAAAAAAACH5BAEAAAAALAAAAAADAAIAAAIChF8AOw=='
    }
  );
  tpl.env.addGlobal('config', window.BZFD.Config);
  return tpl;
}


export default function prepareTemplate(tpl) {
  _attachFilters(tpl);
  _attachGlobals(tpl);
  return tpl;
}
